import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { HexLockup } from '../../../components/FieldCertLogo';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { EyeIcon, Spinner } from '../../../components/ui/auth/AuthIcons';

function validatePassword(value) {
  const pw = String(value || '');
  if (pw.length < 12) return 'Password must be at least 12 characters';
  if (!/[A-Z]/.test(pw)) return 'Must include an uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Must include a lowercase letter';
  if (!/[0-9]/.test(pw)) return 'Must include a number';
  return null;
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [shakeBtn, setShakeBtn] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};

    const passwordError = validatePassword(newPassword);
    if (passwordError) errs.newPassword = passwordError;
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    if (!confirmPassword) errs.confirmPassword = 'Confirm your password';
    if (!token) errs.token = 'Reset token is missing';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setShakeBtn(true);
      setTimeout(() => setShakeBtn(false), 500);
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword });
      toast.success('Password reset successful. Please sign in.');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const ctaClass = [
    'login-cta',
    loading ? 'login-cta--loading' : '',
    shakeBtn ? 'login-cta--shake' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="login-shell login-shell--auth-only">
      <div className="login-shell-bg" aria-hidden="true" />

      <div className="login-auth-panel">
        <div className="login-auth-inner">
          <div className="login-auth-brand">
            <HexLockup iconSize={48} wmSize="sm" gap={12} />
          </div>

          <div className="login-card">
            <h1 className="login-card-heading">Set new password</h1>
            <p className="login-card-lead">Choose a new password for your FieldCert account.</p>

            {errors.token && (
              <div className="login-alert" role="alert" style={{ color: 'var(--danger)', marginBottom: '1rem' }}>
                {errors.token}
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <Input
                label={errors.newPassword || 'New password'}
                error={!!errors.newPassword}
                id="newPassword"
                type={showNew ? 'text' : 'password'}
                endAdornment={(
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowNew((v) => !v)}
                    aria-label={showNew ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <EyeIcon open={showNew} />
                  </button>
                )}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: null }));
                }}
                placeholder="12+ chars, upper, lower, number"
                autoComplete="new-password"
                required
                disabled={loading}
              />

              <Input
                label={errors.confirmPassword || 'Confirm password'}
                error={!!errors.confirmPassword}
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                endAdornment={(
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <EyeIcon open={showConfirm} />
                  </button>
                )}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: null }));
                }}
                autoComplete="new-password"
                required
                disabled={loading}
              />

              <Button type="submit" variant="primary" className={ctaClass} disabled={loading}>
                {loading ? <><Spinner /> Resetting…</> : 'Reset Password'}
              </Button>
            </form>

            <p className="login-card-back">
              <Link to="/login">Back to sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
